/**
 * A voice's loudness, 0..1, as something that can be written forty times a
 * second without re-rendering a screen.
 *
 * The phone keeps it in a Reanimated `SharedValue`: the hooks write it, the orb
 * and the level bars read it on the UI thread, and React never hears of it.
 * The browser has no UI thread to hand it to, so this is the same idea in
 * plain JavaScript: a value with listeners. It has the `SharedValue` surface —
 * `value`, `get`, `set`, `addListener`, `removeListener`, `modify` — so the
 * phone's own writer (`@/lib/orb-level`'s `writeLevelSteps` and `hushLevel`)
 * writes it unchanged, and a `subscribe` for the readers here.
 *
 * Live mode keeps two (`useCoachVoice`): the microphone's (`micLevel`, the
 * dot grid, written for every frame while the person has not muted) and OS's
 * voice (`voiceLevel`, the orb while it speaks). Each has one writer.
 *
 * Readers take it one of two ways:
 *
 *   - `LevelBars` subscribes and moves its bars itself, through refs, so a
 *     level change repaints four pixels and renders nothing.
 *   - `useVoiceLevelValue` hands a component a number, once an animation
 *     frame at most, for a drawing that takes its level as a prop (the orb).
 */
import { useEffect, useState } from 'react';

export type VoiceLevel = {
  /** The level now. Writing it tells every listener. */
  value: number;
  readonly get: () => number;
  readonly set: (next: number | ((held: number) => number)) => void;
  readonly addListener: (id: number, listener: (value: number) => void) => void;
  readonly removeListener: (id: number) => void;
  readonly modify: (modifier?: (held: number) => number, forceUpdate?: boolean) => void;
  /** Told of every write; answers the way to stop being told. */
  readonly subscribe: (listener: (value: number) => void) => () => void;
};

export const createVoiceLevel = (initial = 0): VoiceLevel => {
  let held = initial;
  const listeners = new Map<number | symbol, (value: number) => void>();

  const write = (next: number): void => {
    const value = Number.isFinite(next) ? next : 0;
    if (value === held) return;
    held = value;
    for (const listener of listeners.values()) listener(value);
  };

  return {
    get value() {
      return held;
    },
    set value(next: number) {
      write(next);
    },
    get: () => held,
    set: (next) => write(typeof next === 'function' ? next(held) : next),
    addListener: (id, listener) => {
      listeners.set(id, listener);
    },
    removeListener: (id) => {
      listeners.delete(id);
    },
    modify: (modifier) => {
      if (modifier !== undefined) write(modifier(held));
    },
    subscribe: (listener) => {
      const key = Symbol('level');
      listeners.set(key, listener);
      return () => {
        listeners.delete(key);
      };
    },
  };
};

/** One level for the life of the component that asked for it. */
export const useVoiceLevel = (): VoiceLevel => {
  const [level] = useState(createVoiceLevel);
  return level;
};

/**
 * The level as a number, at most once an animation frame. For a drawing that
 * takes it as a prop; the component that calls this is the only one that
 * re-renders as it moves.
 */
export const useVoiceLevelValue = (level: VoiceLevel): number => {
  const [value, setValue] = useState(level.value);
  useEffect(() => {
    let frame: number | null = null;
    const stop = level.subscribe(() => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setValue(level.value);
      });
    });
    return () => {
      stop();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [level]);
  return value;
};
